/**
 * On-device tap debugger. Off unless switched on, and costs nothing while off.
 *
 * On:  open the app with `?tapdebug=1` in the URL, or hold three fingers on
 *      the screen for about a second. Off: `?tapdebug=0`, or the same gesture.
 *      The setting persists in localStorage.
 *
 * Each touch draws:
 *  - a red dot at the touch's clientX/Y
 *  - a blue outline around document.elementFromPoint(x, y), i.e. what the
 *    browser hit-tests at that point
 *  - a green outline around the element that received `click`, if one came
 *
 * The HUD (top-left; tap it to pin it top or bottom) shows the viewport state
 * and the last few touches: whether click fired or pointercancel ate the tap,
 * the delay, how far the finger moved, and both targets. A dot visibly away
 * from where you tapped, or "no click", is the evidence.
 */

import { rawVisualViewportHeight } from '@/lib/visualViewportFix';

const KEY = 'sundae.tapdebug';

type Tap = {
  x: number;
  y: number;
  t: number;
  moved: number;
  hit: string;
  result: string;
  seq: string[];
};

function describe(el: Element | null): string {
  if (!el) return '∅';
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const cls =
    typeof el.className === 'string' && el.className
      ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
      : '';
  const label =
    el.getAttribute('aria-label') ||
    (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 24);
  return `${tag}${id}${cls}${label ? ` "${label}"` : ''}`;
}

function enabled(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

function setEnabled(on: boolean) {
  try {
    if (on) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode */
  }
}

let teardown: (() => void) | null = null;

function start() {
  if (teardown) return;
  const layer = document.createElement('div');
  layer.style.cssText =
    'position:fixed;inset:0;pointer-events:none;z-index:2147483647;';
  const hud = document.createElement('div');
  let hudBottom = false;
  const placeHud = () => {
    hud.style.cssText =
      'position:fixed;left:4px;max-width:min(96vw,420px);pointer-events:auto;' +
      'z-index:2147483647;background:rgba(0,0,0,.82);color:#fff;font:11px/1.35 ui-monospace,Menlo,monospace;' +
      'padding:6px 8px;border-radius:8px;white-space:pre-wrap;word-break:break-all;' +
      (hudBottom ? 'bottom:4px;' : 'top:calc(env(safe-area-inset-top) + 4px);');
  };
  placeHud();
  hud.addEventListener('click', (e) => {
    e.stopPropagation();
    hudBottom = !hudBottom;
    placeHud();
  });
  document.body.append(layer, hud);

  const taps: Tap[] = [];
  let current: (Tap & { sx: number; sy: number; id: number }) | null = null;

  const box = (el: Element, color: string) => {
    const r = el.getBoundingClientRect();
    const b = document.createElement('div');
    b.style.cssText = `position:fixed;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;outline:2px solid ${color};`;
    layer.append(b);
    setTimeout(() => b.remove(), 2500);
  };
  const dot = (x: number, y: number) => {
    const d = document.createElement('div');
    d.style.cssText = `position:fixed;left:${x - 6}px;top:${y - 6}px;width:12px;height:12px;border-radius:50%;background:red;box-shadow:0 0 0 2px #fff;`;
    layer.append(d);
    setTimeout(() => d.remove(), 2500);
  };

  // Reads env(safe-area-inset-*) as px: a hidden probe padded by the insets.
  const insetProbe = document.createElement('div');
  insetProbe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);';
  document.body.append(insetProbe);
  // Viewport units as px: svh/lvh/dvh disagree when iOS leaves the window short.
  const unitProbe = document.createElement('div');
  const unitPx = (unit: string) => {
    unitProbe.style.cssText = `position:fixed;visibility:hidden;pointer-events:none;width:0;height:100${unit};`;
    return Math.round(unitProbe.getBoundingClientRect().height);
  };
  document.body.append(unitProbe);

  const render = () => {
    const vv = window.visualViewport;
    const ip = getComputedStyle(insetProbe);
    const shell = document.querySelector('.pwa-safe-area');
    const shellRect = shell?.getBoundingClientRect();
    const shellPad = shell ? getComputedStyle(shell).paddingTop : '-';
    const se = document.scrollingElement || document.documentElement;
    const segs = (window as unknown as { viewport?: { segments?: DOMRect[] } })
      .viewport?.segments;
    const posture = (navigator as unknown as { devicePosture?: { type: string } })
      .devicePosture?.type;
    const lines = [
      `inner ${innerWidth}x${innerHeight}  client ${document.documentElement.clientWidth}x${document.documentElement.clientHeight}`,
      vv
        ? `vv ${Math.round(vv.width)}x${Math.round(vv.height)} off ${vv.offsetLeft.toFixed(1)},${vv.offsetTop.toFixed(1)} page ${vv.pageTop.toFixed(1)} scale ${vv.scale.toFixed(3)} rawH ${rawVisualViewportHeight() ?? '?'}`
        : 'vv n/a',
      `doc scroll ${scrollX},${scrollY}  scrollH ${se.scrollHeight} bodyScroll ${document.body.scrollTop}`,
      `safe t${ip.paddingTop} r${ip.paddingRight} b${ip.paddingBottom} l${ip.paddingLeft}`,
      `shell top ${shellRect ? Math.round(shellRect.top) : '-'} h ${shellRect ? Math.round(shellRect.height) : '-'} padTop ${shellPad}`,
      `svh ${unitPx('svh')} lvh ${unitPx('lvh')} dvh ${unitPx('dvh')} vh ${unitPx('vh')}  outer ${outerWidth}x${outerHeight}`,
      `screen ${screen.width}x${screen.height} ${screen.orientation?.type ?? ''}  vis ${document.visibilityState}`,
      `dpr ${devicePixelRatio}  rootfs ${getComputedStyle(document.documentElement).fontSize}  posture ${posture ?? 'n/a'}  segs ${segs ? segs.length : 'n/a'}`,
      '— taps (newest first) —',
      ...taps
        .slice(-4)
        .reverse()
        .map(
          (t) =>
            `(${Math.round(t.x)},${Math.round(t.y)}) mv${Math.round(t.moved)} ${t.result}\n   hit: ${t.hit}\n   seq: ${t.seq.join(' ')}`,
        ),
    ];
    hud.textContent = lines.join('\n');
  };

  const onDown = (e: PointerEvent) => {
    if (hud.contains(e.target as Node)) return;
    const hit = document.elementFromPoint(e.clientX, e.clientY);
    lastHit = hit;
    // Draw nothing yet: WebKit's content-change observer watches the touch,
    // and visible DOM appearing during it makes iOS skip the click (that is
    // exactly the bug class this tool hunts). Visuals are queued and drawn
    // once the tap has resolved.
    const x = e.clientX, y = e.clientY;
    pending.push(() => {
      dot(x, y);
      if (hit && hit.isConnected) box(hit, '#2b7bff');
    });
    if (current) finish('superseded');
    current = {
      x: e.clientX,
      y: e.clientY,
      sx: e.clientX,
      sy: e.clientY,
      id: e.pointerId,
      t: performance.now(),
      moved: 0,
      hit: `${describe(hit)} cur:${hit ? getComputedStyle(hit).cursor : '-'}`,
      result: 'pending',
      seq: [...preDown, 'pd'],
    };
    preDown = [];
    taps.push(current);
    if (taps.length > 20) taps.shift();
  };
  const onMove = (e: PointerEvent) => {
    if (!current || e.pointerId !== current.id) return;
    current.moved = Math.max(
      current.moved,
      Math.hypot(e.clientX - current.sx, e.clientY - current.sy),
    );
  };
  let pending: Array<() => void> = [];
  const finish = (result: string) => {
    if (!current) return;
    current.result = result;
    current = null;
    // Still defer a beat past the click so drawing never lands inside the
    // touch sequence.
    const draw = pending;
    pending = [];
    setTimeout(() => {
      draw.forEach((f) => f());
      render();
    }, 50);
  };
  let upTimer: ReturnType<typeof setTimeout> | null = null;
  const onUp = (e: PointerEvent) => {
    if (!current || e.pointerId !== current.id) return;
    const tap = current;
    // A click normally follows pointerup within a few ms; if none arrives,
    // the tap was swallowed (gesture handler, hover-first tap, or no target).
    if (upTimer) clearTimeout(upTimer);
    upTimer = setTimeout(() => {
      if (current === tap) finish(`NO CLICK (up after ${Math.round(performance.now() - tap.t)}ms)`);
    }, 1500);
  };
  const onCancel = (e: PointerEvent) => {
    if (current && e.pointerId === current.id) finish('POINTERCANCEL (scroll/gesture took it)');
  };
  const onClick = (e: MouseEvent) => {
    if (hud.contains(e.target as Node)) return;
    const target = e.target as Element;
    pending.push(() => {
      if (target.isConnected) box(target, '#18c964');
    });
    if (current) {
      const dt = Math.round(performance.now() - current.t);
      const same = describe(target) === current.hit;
      finish(`click ${dt}ms${same ? '' : `\n   click on: ${describe(target)}`}`);
    }
  };

  // Event sequence per tap, to see where iOS stops: abbreviations
  // ts/te touchstart/end (P = defaultPrevented by the time it reached window,
  // i.e. some handler cancelled it), pd/pu pointerdown/up, pc pointercancel,
  // mo/mm/md/mu mouseover/move/down/up, fi focusin, c click, xD = the hit
  // element was detached from the DOM by then.
  let preDown: string[] = [];
  let lastHit: Element | null = null;
  const note = (code: string) => {
    if (current) current.seq.push(code);
    else preDown.push(code);
    if (preDown.length > 6) preDown.shift();
  };
  const seqEvents: Array<[string, string]> = [
    ['touchstart', 'ts'], ['touchend', 'te'], ['touchcancel', 'tc'],
    ['pointerup', 'pu'], ['pointercancel', 'pc'],
    ['mouseover', 'mo'], ['mousemove', 'mm'], ['mousedown', 'md'], ['mouseup', 'mu'],
    ['focusin', 'fi'],
  ];
  const seqHandler = (code: string) => (e: Event) => {
    let c = code;
    if (e.defaultPrevented) c += 'P';
    if (code === 'ts' && current) {
      /* new touch before resolution */
    }
    if (lastHit && !lastHit.isConnected) c += 'xD';
    note(c);
  };
  const seqListeners = seqEvents.map(([type, code]) => {
    const h = seqHandler(code);
    // Bubble phase on window: runs after the app's own handlers, so a
    // cancel by them shows up as P. (stopPropagation would hide the event
    // entirely, which also shows: it's missing from the sequence.)
    window.addEventListener(type, h, { passive: true });
    return () => window.removeEventListener(type, h);
  });
  const clickSeq = (e: Event) => note('c' + (e.defaultPrevented ? 'P' : ''));
  window.addEventListener('click', clickSeq, { passive: true });

  const opts = { capture: true, passive: true } as const;
  window.addEventListener('pointerdown', onDown, opts);
  window.addEventListener('pointermove', onMove, opts);
  window.addEventListener('pointerup', onUp, opts);
  window.addEventListener('pointercancel', onCancel, opts);
  window.addEventListener('click', onClick, opts);
  window.visualViewport?.addEventListener('resize', render);
  window.visualViewport?.addEventListener('scroll', render);
  window.addEventListener('scroll', render, opts);
  // Resume-time layout: re-read after the app comes back, and again once iOS
  // has settled its geometry (insets can arrive a beat after visibility).
  const onResume = () => {
    render();
    setTimeout(render, 500);
    setTimeout(render, 2000);
  };
  const resumeEvents = ['resize', 'orientationchange', 'pageshow', 'focus'] as const;
  resumeEvents.forEach((t) => window.addEventListener(t, onResume));
  document.addEventListener('visibilitychange', onResume);
  render();

  teardown = () => {
    seqListeners.forEach((off) => off());
    window.removeEventListener('click', clickSeq);
    window.removeEventListener('pointerdown', onDown, opts);
    window.removeEventListener('pointermove', onMove, opts);
    window.removeEventListener('pointerup', onUp, opts);
    window.removeEventListener('pointercancel', onCancel, opts);
    window.removeEventListener('click', onClick, opts);
    window.visualViewport?.removeEventListener('resize', render);
    window.visualViewport?.removeEventListener('scroll', render);
    window.removeEventListener('scroll', render, opts);
    resumeEvents.forEach((t) => window.removeEventListener(t, onResume));
    document.removeEventListener('visibilitychange', onResume);
    insetProbe.remove();
    unitProbe.remove();
    layer.remove();
    hud.remove();
    teardown = null;
  };
}

export function initTapDebug() {
  const href = window.location.href;
  if (/[?&]tapdebug=1\b/.test(href)) setEnabled(true);
  if (/[?&]tapdebug=0\b/.test(href)) setEnabled(false);

  // Three-finger hold (~1s) toggles it — works in a home-screen app with no URL bar.
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  window.addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length !== 3) return;
      if (holdTimer) clearTimeout(holdTimer);
      holdTimer = setTimeout(() => {
        const on = !enabled();
        setEnabled(on);
        if (on) start();
        else teardown?.();
      }, 900);
    },
    { passive: true },
  );
  const cancel = () => {
    if (holdTimer) clearTimeout(holdTimer);
    holdTimer = null;
  };
  window.addEventListener('touchend', cancel, { passive: true });
  window.addEventListener('touchcancel', cancel, { passive: true });

  if (enabled()) {
    if (document.body) start();
    else window.addEventListener('DOMContentLoaded', start, { once: true });
  }
}
